import { inject, Injectable } from '@angular/core';
import { DecaStopType, IDecaDocument, UUID, UUID_NULL } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { DecaCommandService } from '@okcargo-apps/shared-infrastructure';
import { AccountService } from '../account';

@Injectable({
  providedIn: 'root',
})
export class DecaMutationsService {
  private readonly decaCommandService = inject(DecaCommandService);
  private readonly accountService = inject(AccountService);

  public async createOrUpdateDocumentAsShipper(document: IDecaDocument, changeReason = ''): Promise<void> {
    // TODO if necessary, add business rule validation here, for example:
    // if (!document.origin?.date || !document.destination?.date) {
    //   throw new Error('Origin and destination dates are required to process the control document.');
    // }

    const isNew = !document.id || document.id === UUID_NULL;

    if (isNew) {
      await this.decaCommandService.createDocumentAsShipper(document);
    } else {
      await this.decaCommandService.updateDocumentAsShipper(document, changeReason);
    }
  }

  public async createOrUpdateDocumentAsCarrier(document: IDecaDocument, changeReason = ''): Promise<void> {
    // business rule validation was added to the orchestrator component
    // if (!document.origin?.date || !document.destination?.date) {
    //   throw new Error('Origin and destination dates are required to process the control document.');
    // }

    const isNew = !document.id || document.id === UUID_NULL;

    if (isNew) {
      await this.decaCommandService.createDocumentAsCarrier(document);
    } else {
      await this.decaCommandService.updateDocumentAsCarrier(document, changeReason);
    }
  }

  public async addReservation(document: IDecaDocument): Promise<void> {
    await this.decaCommandService.addReservation(document);
  }

  public async updatePlateNumbers(document: IDecaDocument, changeReason = ''): Promise<void> {
    await this.decaCommandService.updatePlateNumbers(document, changeReason);
  }

  public async updateStopDate(
    document: IDecaDocument,
    stopType: DecaStopType,
    segmentId: UUID,
    changeReason = '',
  ): Promise<void> {
    if (stopType === 'origin') {
      await this.decaCommandService.updateOriginDate(document, segmentId, changeReason);
    } else {
      await this.decaCommandService.updateDestinationDate(document, segmentId, changeReason);
    }
  }

  public async updateSpecialAuthorization(document: IDecaDocument, changeReason = ''): Promise<void> {
    await this.decaCommandService.updateSpecialAuthorization(document, changeReason);
  }

  public async updateGoodsDescription(document: IDecaDocument, segmentId: UUID, changeReason = ''): Promise<void> {
    await this.decaCommandService.updateGoodsDescription(document, segmentId, changeReason);
  }

  public async updateNetWeight(document: IDecaDocument, segmentId: UUID, changeReason = ''): Promise<void> {
    await this.decaCommandService.updateNetWeight(document, segmentId, changeReason);
  }

  public async updateStopAddress(
    document: IDecaDocument,
    stopType: DecaStopType,
    segmentId: UUID,
    changeReason = '',
  ): Promise<void> {
    if (stopType === 'origin') {
      await this.decaCommandService.updateOriginAddress(document, segmentId, changeReason);
    } else {
      await this.decaCommandService.updateDestinationAddress(document, segmentId, changeReason);
    }
  }

  public async requestPromotionalCredits(): Promise<void> {
    if (this.accountService.hasCapability(ECapabilities.CREATE_DECA_AS_CARRIER)) {
      return this.decaCommandService.requestCreditsAsCarrier();
    }

    if (this.accountService.hasCapability(ECapabilities.CREATE_DECA_AS_SHIPPER)) {
      return this.decaCommandService.requestCreditsAsShipper();
    }
  }

  public async uploadFile(file: File): Promise<void> {
    if (!file) {
      throw new Error('File is required');
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (readerEvent: ProgressEvent<FileReader>) => {
        try {
          const fileData = (readerEvent.target as FileReader).result as string;
          const base64Data = fileData.substring(fileData.indexOf(',') + 1);

          await this.decaCommandService.uploadFileCreateDocumentBatchAsShipper(base64Data);
          resolve();
        } catch (error) {
          // TODO manage error
          console.error('Error uploading file:', error);
          reject(error);
        }
      };

      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }
}
